import {useDispatch, useSelector} from "react-redux";
import React, {useState, useEffect} from "react";
import {createAnswer, updateAnswer} from "../../store/app/app.slice";
import {Form, message, Modal, Select} from "antd";
import TextArea from "antd/es/input/TextArea";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {CreateAnswerDto} from "ps-letter-mgmtrest-client";
import {getTopicsOptions, getTopicsOptionsUrdu} from "../helper/labelHelper";
import {AnswerDto} from "ps-letter-mgmtrest-client";

const { Option } = Select;

export default function CreateAnswerDialog(props) {

    const dispatch = useDispatch();
    
    const [topics, setTopics] = useState(props.answer ? props.answer.topics : (props?.topic) ? [props.topic] : []);
    const [selectedSummaries, setSelectedSummaries] = useState(props.answer ? props.answer.summaries.map(summary => summary.id) : (props?.summary) ? [props.summary] : []);
    const [titleEnglish, setTitleEnglish] = useState(props.answer?.titleEnglish || props.defaultTitle || "");
    const [answerEnglish, setAnswerEnglish] = useState(props.answer?.answerEnglish || "");
    const [quillKey, setQuillKey] = useState(0);
    const handleAnswerEnglishChange = (val, delta, source) => {
        // Only the editor's own edits count. ReactQuill also fires onChange
        // with source "api" when it normalises the HTML handed to it on mount,
        // which can strip content (e.g. removing <p> tags) and would overwrite
        // an answer loaded from props.
        //
        // This used to ignore the *first* onChange instead, whichever one that
        // was. On a new answer there is nothing for Quill to normalise, so that
        // call never arrives and the guard ate the user's first real edit: a
        // Ctrl+V pasted nothing, because the paste was the first onChange and
        // only the next keystroke got through.
        if (source !== 'user') {
            return;
        }
        setAnswerEnglish(val);
    };
    
    const [titleUrdu, setTitleUrdu] = useState("");
    const [answerUrdu, setAnswerUrdu] = useState("");
    const [answerGerman, setAnswerGerman] = useState("");
    const {summaries} = useSelector((state) => state.app);
    const isUrduLanguage = props.isUrduLanguage

    // Force ReactQuill to remount every time the modal opens
    useEffect(() => {
        if (props.showModal) {
            setQuillKey(prev => prev + 1);
        }
    }, [props.showModal]);

    // Update state when answer prop changes (for editing)
    useEffect(() => {
        if (props.answer) {
            setTopics(props.answer.topics || []);
            setSelectedSummaries(props.answer.summaries ? props.answer.summaries.map(summary => summary.id) : []);
            setTitleEnglish(props.answer.titleEnglish || "");
            setAnswerEnglish(props.answer.answerEnglish || "");
        }
    }, [props.answer]);

    // Update title when defaultTitle changes (for new answer from summary)
    useEffect(() => {
        if (!props.answer && props.defaultTitle) {
            setTitleEnglish(props.defaultTitle);
        }
    }, [props.defaultTitle, props.answer]);

    // Auto-select summary and inherit its topics when creating from a summary
    useEffect(() => {
        if (!props.answer && props.summary && summaries) {
            const summaryId = typeof props.summary === 'object' ? props.summary.id : props.summary;
            const found = summaries.find((s) => s.id === summaryId);
            if (found) {
                setSelectedSummaries([found.id]);
                if (found.topics && found.topics.length > 0) {
                    setTopics(Array.isArray(found.topics) ? found.topics : Array.from(found.topics));
                }
            }
        }
    }, [props.summary, props.answer, summaries]);

    // Handler to update topics when summaries are changed
    const handleSummaryChange = (newSelectedSummaries) => {
        setSelectedSummaries(newSelectedSummaries);

        // Collect all topics from selected summaries
        const allTopics = new Set();
        newSelectedSummaries.forEach(summaryId => {
            const summary = summaries.find(s => s.id === summaryId);
            if (summary && summary.topics) {
                const summaryTopics = Array.isArray(summary.topics) ? summary.topics : Array.from(summary.topics);
                summaryTopics.forEach(topic => allTopics.add(topic));
            }
        });

        // Update topics with the collected topics from all selected summaries
        setTopics(Array.from(allTopics));
    };
    
    const hideModal = () =>  {
        props.onHideModal()
    }
    
    const handleOk = () =>  {
        if (!selectedSummaries || selectedSummaries.length === 0) {
            message.error({content: "Please select at least one summary"});
            return;
        }
        const selectedSummariesItem = selectedSummaries?.map((summary) => summaries.find((item) => item.id === summary));
        if(props.answer)
        {
            const newAnswer = {
                ...props.answer,
                titleEnglish: titleEnglish,
                answerEnglish: answerEnglish,
                summaries: selectedSummariesItem,
                topics: topics
            }
            dispatch(updateAnswer({
                finalData: {
                    answer: newAnswer
                },
                callbackSuccess: (result) => {
                    console.log(result)
                    if (props.onAnswerUpdated) {
                        props.onAnswerUpdated(result);
                    }
                    hideModal();
                },
                callbackError: (error) => {
                    console.log(error)
                    message.error({content: JSON.stringify(error)})
                },
            }));
            
        }else{
            const newAnswer = {
                titleUrdu: titleUrdu,
                titleEnglish: titleEnglish,
                answerEnglish: answerEnglish,
                answerGerman: answerGerman,
                answerUrdu: answerUrdu,
                summaries: selectedSummariesItem,
                answerLanguages: [props.letterLanguage],
                topics: topics
            }
            dispatch(createAnswer({
                finalData: {
                    answer: newAnswer
                },
                callbackSuccess: (result) => {
                    console.log(result)
                    if (props.onAnswerCreated) {
                        props.onAnswerCreated(result);
                    }
                    setAnswerGerman("")
                    setAnswerEnglish("")
                    setAnswerUrdu("")
                    setTitleUrdu("")
                    setTitleEnglish(undefined)
                    hideModal();
                },
                callbackError: (error) => {
                    console.log(error)
                    message.error({content: JSON.stringify(error)})
                },
            }));
        }
        
        
    }
    
    return <Modal
            open={props.showModal}
            title={props.answer ? "Edit Answer" : "Create a new Answer"}
            onOk={handleOk}
            okText={"Ok"}
            onCancel={hideModal}
            width={1000}
        >
        <Form.Item label="Answer title">
            <TextArea  className={isUrduLanguage ? "arabic-font arabic-font-edit" : "arabic-font arabic-font-edit"} rows={1} size={"large"} value={titleEnglish}
                       onChange={e => setTitleEnglish(e.target.value)}/>
        </Form.Item>
        {/*<Form.Item label="Answer title in Urdu">*/}
        {/*    <TextArea  className={"arabic-font arabic-font-edit"} rows={1} size={"large"} value={titleUrdu}*/}
        {/*                 onChange={e => setTitleUrdu(e.target.value)}/>*/}
        {/*</Form.Item>*/}

        <Form.Item label="Answer Text">
            <ReactQuill
                key={quillKey}
                className={isUrduLanguage ? "arabic-font arabic-font-edit" : ""}
                value={answerEnglish}
                onChange={handleAnswerEnglishChange}
                modules={{
                    toolbar: [
                        ['bold', 'underline']
                    ],
                    keyboard: { bindings: { tab: true } }
                }}
                style={{marginBottom: '20px', width: '100%'}}
            />
        </Form.Item>
        
        {/*<Form.Item label="Answer in German">*/}
        {/*    <TextArea  rows={4} size={"large"} value={answerGerman}*/}
        {/*               onChange={e => setAnswerGerman(e.target.value)}/>*/}
        {/*</Form.Item>*/}
        
        {/*<Form.Item label="Answer in Urdu">*/}
        {/*    <TextArea  className={"arabic-font arabic-font-edit"} rows={4} size={"large"} value={answerUrdu}*/}
        {/*               onChange={e => setAnswerUrdu(e.target.value)}/>*/}
        {/*</Form.Item>*/}

        <Form.Item label="Topics">
            <Select
                mode="multiple"
                style={{width: '100%'}}
                allowClear
                placeholder={"Choose Topics"}
                popupClassName={isUrduLanguage ? "arabic-font arabic-font-edit" : ""}
                className={isUrduLanguage ? "arabic-font arabic-font-extra-small": ""}
                value={topics}
                onChange={setTopics}
            >
                {isUrduLanguage ? getTopicsOptionsUrdu() : getTopicsOptions()}
            </Select>
        </Form.Item>

        <Form.Item label="Summaries">
            <Select
                mode="multiple"
                style={{width: '100%'}}
                allowClear
                popupClassName={"arabic-font arabic-font-edit"}
                className={"arabic-font arabic-font-extra-small"}
                placeholder={"Choose Summaries"}
                value={selectedSummaries}
                onChange={handleSummaryChange}
                fieldNames={{ label: "titleUrdu", value: "id"}}
                options={summaries}
                option
            />
        </Form.Item>
        </Modal>
}